import {
  ChangeEventHandler,
  createElement,
  FC,
  useEffect,
  useRef,
} from "react";
import styles from "./TextareaInputGroup.module.scss";
import classNames from "classnames";
import { sanitizeInput, containsDangerousContent } from "../../utils/sanitize";

interface ITextareaInputGroupProps {
  placeholder: string;
  label: string;
  name: string;
  value: string;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  disabled: boolean;
  error?: string | false;
  icon?: React.ElementType;
  isFocused?: boolean;
  setIsFocused: (e: boolean) => void;
  autosize?: boolean;
  minRows?: number;
  maxLength?: number;
  showCharCounter?: boolean;
}

export const TextareaInputGroup: FC<ITextareaInputGroupProps> = ({
  placeholder,
  label,
  name,
  value,
  onChange,
  onBlur,
  disabled,
  error,
  icon,
  isFocused,
  setIsFocused,
  autosize = false,
  minRows = 1,
  maxLength = 300,
  showCharCounter = true,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const handleResize = () => {
    const textarea = textareaRef.current;
    if (textarea && autosize) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value;

    if (containsDangerousContent(inputValue)) {
      console.warn("Potentially dangerous content detected and sanitized");
      e.target.value = sanitizeInput(inputValue);
    }

    onChange(e);
    handleResize();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = e.clipboardData.getData("text");

    if (containsDangerousContent(pastedText)) {
      e.preventDefault();
      const sanitizedText = sanitizeInput(pastedText);
      const target = e.target as HTMLTextAreaElement;
      target.value = sanitizedText;

      const changeEvent = new Event("input", { bubbles: true });
      target.dispatchEvent(changeEvent);

      console.warn(
        "Potentially dangerous content detected in paste and sanitized"
      );
    }
  };

  useEffect(() => {
    handleResize();
  }, [value]);

  useEffect(() => {
    if (textareaRef.current && autosize) {
      const lineHeight = parseInt(
        window.getComputedStyle(textareaRef.current).lineHeight || "20"
      );
      textareaRef.current.style.minHeight = `${lineHeight * minRows}px`;
    }
  }, [minRows, autosize]);

  const isOverLimit = value.length > maxLength;
  const charCount = value.length;

  return (
    <div
      className={classNames(styles.formGroup, {
        [styles.focused]: isFocused,
        [styles.error]: !!error,
        [styles.disabled]: disabled,
      })}
    >
      <div className={styles.inputContainer}>
        <div className={styles.icon}>
          {icon && createElement(icon, { size: "1.2rem", stroke: 1.5 })}
        </div>
        <div
          className={classNames(styles.label, { [styles.focused]: isFocused })}
        >
          {label}
        </div>
        <textarea
          ref={textareaRef}
          spellCheck="false"
          id={name}
          placeholder={placeholder}
          name={name}
          value={value}
          maxLength={maxLength}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={(e) => {
            onBlur(e);
            setIsFocused(false);
          }}
          onFocus={() => setIsFocused(true)}
          disabled={disabled}
          rows={minRows}
        />
        {showCharCounter && (
          <div
            className={classNames(styles.charCounter, {
              [styles.overLimit]: isOverLimit,
            })}
          >
            {charCount}/{maxLength}
          </div>
        )}
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    </div>
  );
};
